import { Helmet } from 'react-helmet-async'
import { useLocation } from 'react-router-dom'
import { HOME_QUESTIONS, HOME_QUESTIONS_EN } from '@/sections/Faq/questions'
import { CONTENT_PUBLISHED, CONTENT_UPDATED } from '@/data/site-meta'
import { absoluteUrl, localeFromPath } from '@/i18n/locale'
import { TG_PERSONAL_URL, TG_CHANNEL_URL } from '@/lib/contacts'
import { canonicalPath } from '@/lib/sections'

/**
 * SEO + schema.org для лендинга.
 * Базовые мета-теги — в index.html (первая отрисовка без JS).
 * Здесь — structured data:
 *   - Organization (с founder, contactPoint, address)
 *   - Service — один продукт, без цены: цены на сайте нет (Тони 2026-09-27),
 *     стоимость считаем на бесплатной диагностике
 *   - FAQPage (источник: HOME_QUESTIONS из src/sections/Faq/questions.ts —
 *     та же шестёрка, что видна на главной; полный список размечен на /faq)
 *   - WebPage с датами из src/data/site-meta.ts
 *
 * i18n: на /en текстовые поля schema.org — английские (FAQPage из
 * HOME_QUESTIONS_EN, чтобы разметка 1:1 совпадала с видимым текстом секции).
 * Юридические факты (legalName, ИНН, адрес) не переводятся.
 */
export function Seo() {
  // canonical у адресов блоков (/roi, /cases…) — главная: это одна страница.
  const pathname = canonicalPath(useLocation().pathname)
  const locale = localeFromPath(pathname)
  const isEn = locale === 'en'
  const selfCanonical = absoluteUrl(pathname, locale)

  const org = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'DNAI Engineering',
    alternateName: ['ДНАИ Инжиниринг', 'DNAI'],
    slogan: isEn
      ? 'AI engineering and architecture for business'
      : 'ИИ-инжиниринг и архитектура для бизнеса',
    legalName: 'ИП Ро Батень Антон Сергеевич',
    url: 'https://dnai.engineering/',
    logo: 'https://dnai.engineering/og-image.png',
    description: isEn
      ? 'AI employees for margin growth and business scaling. Not just automation — AI architecture with KPIs on your P&L.'
      : 'Цифровые сотрудники с ИИ для роста маржи и масштабирования бизнеса. Не просто автоматизация — ИИ-архитектура с KPI на P&L.',
    founder: {
      '@type': 'Person',
      name: isEn ? 'Anton Ro Baten' : 'Антон Ро Батень',
      jobTitle: isEn
        ? 'AI architect, founder of DNAI Engineering'
        : 'ИИ-архитектор, основатель DNAI Engineering',
      description: isEn
        ? '65 business scaling cases. 15 years systematizing and scaling companies, the last year in AI transformation.'
        : '65 кейсов масштабирования бизнеса. 15 лет в систематизации и масштабировании компаний, из них год — в ИИ-трансформации.',
      sameAs: [TG_CHANNEL_URL, TG_PERSONAL_URL],
    },
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'a.robaten@gmail.com',
      url: TG_PERSONAL_URL,
      availableLanguage: ['Russian', 'English'],
    },
    address: {
      '@type': 'PostalAddress',
      addressCountry: 'RU',
      addressRegion: 'Московская область',
      addressLocality: 'Мытищи',
      postalCode: '141021',
    },
    taxID: '502912488625',
    sameAs: [TG_CHANNEL_URL, TG_PERSONAL_URL],
    areaServed: ['RU', 'BY', 'KZ', 'UZ', 'KG', 'AM', 'GE', 'AZ', 'MD'],
  }

  // Один продукт (мандат 2026-08-01): внедрение ИИ-экосистемы. Клуб On vAIbe —
  // бонус внутри продукта, отдельно не продаётся (решение Тони 2026-09-20):
  // страницы /club нет, поэтому отдельного Service с ценой в разметке быть не
  // должно — она объявляла бы поисковику товар, который негде купить.
  const serviceCopy = isEn
    ? [
        {
          name: 'AI Ecosystem Implementation for Business',
          description:
            'One product: the executive team headed by the CEO and the owner works inside an AI ecosystem on corporate licenses registered to your company, while CRM, ERP and telephony become data sources; management runs through AI. Session-based team training, the On vAIbe club and ongoing support are included in the product.',
        },
      ]
    : [
        {
          name: 'Внедрение ИИ-экосистемы в бизнес',
          description:
            'Один продукт: команда топов во главе с гендиректором и собственником работает в ИИ-экосистеме на корпоративных лицензиях, оформленных на вашу компанию, а CRM, 1С и телефония становятся источниками данных; управление идёт через ИИ. Обучение команды сессиями, клуб On vAIbe и сопровождение — входят в продукт.',
        },
      ]

  const services = serviceCopy.map((s) => ({
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: s.name,
    description: s.description,
    provider: { '@type': 'Organization', name: 'DNAI Engineering' },
    areaServed: isEn ? 'CIS' : 'СНГ',
  }))

  const faqPage = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: (isEn ? HOME_QUESTIONS_EN : HOME_QUESTIONS).map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.a,
      },
    })),
  }

  // Даты — константы из site-meta, а не время сборки: еженедельная пересборка
  // пустым коммитом иначе двигала бы dateModified без единой правки текста.
  const webPage = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    url: selfCanonical,
    datePublished: CONTENT_PUBLISHED,
    dateModified: CONTENT_UPDATED,
    inLanguage: isEn ? 'en' : 'ru',
  }

  return (
    <Helmet>
      {/* Canonical — self-URL под текущую локаль (/ или /en), чтобы
          английская версия не канонизировалась на русскую главную.
          hreflang-связку эмитит SeoLocale. */}
      <link rel="canonical" href={selfCanonical} />
      <script type="application/ld+json">{JSON.stringify(org)}</script>
      {services.map((s, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(s)}
        </script>
      ))}
      <script type="application/ld+json">{JSON.stringify(faqPage)}</script>
      <script type="application/ld+json">{JSON.stringify(webPage)}</script>
    </Helmet>
  )
}
